import {
    ChangeDetectorRef, Component, Injector, OnInit, TemplateRef, Type,
    ViewChild, ViewContainerRef, inject
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { DIALOG_DATA } from '@angular/cdk/dialog';
import { NgbActiveModal as VendoredActiveModal } from '@otas/modal/modal-ref';
import { NgbActiveModal as NgBootstrapActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { APP_MODAL_DATA, AppModalButton, AppModalOptions, AppModalRef } from './app-modal.types';

/** Payload AppModalService hands the shell through DIALOG_DATA. */
export interface AppModalShellData {
    content: Type<unknown> | TemplateRef<unknown>;
    options: AppModalOptions;
    ref: AppModalRef;
}

/**
 * The modal CARD (chrome shell): header (title + top-right ✕), scrollable
 * body hosting the content, optional footer button bar. Opened only by
 * `AppModalService` inside a CDK Dialog overlay.
 *
 * Component content is created with an element injector that provides
 * `AppModalRef`, `APP_MODAL_DATA` and BOTH `NgbActiveModal` classes (vendored
 * `@otas/modal` + npm ng-bootstrap) bridged onto the ref — so any legacy
 * modal component's `activeModal.close()/dismiss()` works unchanged here.
 * Template content receives `{ $implicit: ref, close, dismiss, data }`.
 */
@Component({
    selector: 'app-modal',
    templateUrl: './app-modal.component.html',
    styleUrls: ['./app-modal.component.css'],
    imports: [NgTemplateOutlet],
    // Default (not OnPush) change detection: the hosted content is arbitrary
    // legacy/Default-CD components (booking modals etc.). An OnPush host is only
    // re-checked when marked, so it would freeze its whole content subtree —
    // the timepicker never showing its digits and the datepicker calendar not
    // reacting were exactly that. Default CD lets hosted content tick normally.
    host: { '[class]': 'hostClass' },
})
export class AppModalComponent implements OnInit {
    private readonly shellData = inject<AppModalShellData>(DIALOG_DATA);
    private readonly injector = inject(Injector);
    private readonly cdr = inject(ChangeDetectorRef);

    readonly options: AppModalOptions = this.shellData.options || {};
    readonly ref = this.shellData.ref;
    readonly title = this.options.title || '';
    readonly showClose = this.options.showClose !== false;
    readonly buttons: readonly AppModalButton[] = this.options.buttons || [];
    readonly templateContent: TemplateRef<unknown> | null =
        this.shellData.content instanceof TemplateRef ? this.shellData.content : null;
    readonly templateContext = {
        $implicit: this.ref,
        close: (value?: unknown) => this.ref.close(value),
        dismiss: (reason?: unknown) => this.ref.dismiss(reason),
        data: this.options.data,
    };

    /** Anchored ConfirmPopup placement (arrow + connected position). */
    readonly isPopup = !!this.options.confirmPopup;

    readonly hostClass =
        'am-card am-' + (this.options.size || 'md')
        // 'below' is the preferred placement; AppModalService flips the class
        // to am-popup-above when CDK has to place the panel above the anchor.
        + (this.isPopup ? ' am-popup am-popup-below' : '')
        + (this.options.modalClass ? ' ' + this.options.modalClass : '');

    @ViewChild('contentHost', { read: ViewContainerRef, static: true })
    private contentHost!: ViewContainerRef;

    ngOnInit(): void {
        if (this.templateContent) {
            return;
        }
        const vendored = new VendoredActiveModal();
        vendored.close = (value?: unknown) => this.ref.close(value);
        vendored.dismiss = (reason?: unknown) => this.ref.dismiss(reason);
        vendored.hide = (value?: unknown) => this.ref.close(value);
        const ngBootstrap = new NgBootstrapActiveModal();
        ngBootstrap.close = (value?: unknown) => this.ref.close(value);
        ngBootstrap.dismiss = (reason?: unknown) => this.ref.dismiss(reason);
        const contentInjector = Injector.create({
            providers: [
                { provide: AppModalRef, useValue: this.ref },
                { provide: APP_MODAL_DATA, useValue: this.options.data ?? null },
                { provide: VendoredActiveModal, useValue: vendored },
                { provide: NgBootstrapActiveModal, useValue: ngBootstrap },
                ...(this.options.providers || []),
            ],
            // Chains to the caller's injector when options.injector was passed
            // to AppModalService (that injector built this shell).
            parent: this.injector,
        });
        const componentRef = this.contentHost.createComponent(
            this.shellData.content as Type<unknown>, { injector: contentInjector });
        this.ref.componentInstance = componentRef.instance;
        // LEGACY NgbModal CONTRACT (owner live-smoke, calendar round 11): the
        // opener sets `componentInstance` fields SYNCHRONOUSLY after open()
        // and the content's ngOnInit must SEE them. AppModalService.open()
        // drives this ngOnInit synchronously (so the instance exists on
        // return), therefore the content's own first change detection — the
        // pass that runs its ngOnInit — is DETACHED until a microtask, giving
        // the opener's synchronous assignments their legacy window. Without
        // this, `instance.fromDate = ...` threw on undefined and the opener's
        // .result.then(...) was never attached — the continuous-booking
        // submit resolved into a promise nobody listened to.
        componentRef.changeDetectorRef.detach();
        // Render the shell chrome synchronously; the content renders in the
        // microtask below — still before the first paint, so the modal is
        // laid out at its FINAL size (paired with the CSS centre on
        // `.cdk-overlay-pane.app-modal-pane` — no top-left flash).
        this.cdr.detectChanges();
        Promise.resolve().then(() => {
            if (componentRef.hostView.destroyed) {
                return; // opened and closed within one task
            }
            componentRef.changeDetectorRef.reattach();
            componentRef.changeDetectorRef.detectChanges();
            // One more shell pass so nested OnPush CVAs (time-picker digits,
            // date-picker display) settle before the user sees the card.
            this.cdr.detectChanges();
        });
    }

    onButton(button: AppModalButton): void {
        if (button.action) {
            // The content decides the result AND whether to close (see
            // AppModalButton.action) — the shell must not settle the ref.
            button.action(this.ref);
            return;
        }
        if (button.role === 'dismiss') {
            this.ref.dismiss('button');
        } else {
            this.ref.close(button.value);
        }
    }

    buttonClass(button: AppModalButton): string {
        return 'am-btn am-btn-' + (button.kind || 'primary');
    }

    /** The `<kbd>` chip text for a button's hotkey ('' when none). */
    hotkeyLabel(button: AppModalButton): string {
        if (button.hotkey === 'enter') {
            return 'ENTER';
        }
        if (button.hotkey === 'esc') {
            return 'ESC';
        }
        if (button.hotkey === 'f1') {
            return 'F1';
        }
        return '';
    }

    /** Hover tooltip for a hotkey button (null when none). */
    hotkeyTip(button: AppModalButton): string | null {
        if (!button.hotkey) {
            return null;
        }
        if (button.hotkeyTip) {
            return button.hotkeyTip;
        }
        if (button.hotkey === 'enter') {
            return 'Click ENTER to confirm';
        }
        return button.hotkey === 'f1' ? 'Press F1 to submit' : 'Press ESC to close';
    }
}
